import { LocationDefinition } from '../types/map.types';

export const LOCATION_STAIRS_DOWN: LocationDefinition = {
  id: 'stairs-down',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 0,
  artAnchor: [0.5, 1],
  mandatory: true,
  unique: true,
  sprite: 'stairs',
  interactionType: 'interact',
  mapCategory: 'exit',
  effects: [{ handler: 'nextFloor' }],
};

export const LOCATION_SPAWN_POINT: LocationDefinition = {
  id: 'spawn-point',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 0,
  mandatory: true,
  unique: true,
  interactionType: 'none',
};

export const LOCATION_CHEST: LocationDefinition = {
  id: 'chest',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 30,
  maxCount: 3,
  oncePerPlayer: true,
  sprite: 'caisse',
  interactionType: 'interact',
  mapCategory: 'chest',
  effects: [
    { handler: 'playSound', params: { id: 'chest' } },
    {
      handler: 'spawnItemsFromPool',
      params: { pool: 'map-loot', maxCount: 3, radius: 1 },
    },
    { handler: 'spawnBoost', params: { source: 'chest-common' } },
    { handler: 'markPoiUsed' },
    { handler: 'triggerPoiBreakAnimation' },
  ],
};

export const LOCATION_URN: LocationDefinition = {
  id: 'urn',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 40,
  maxCount: 4,
  oncePerPlayer: true,
  sprite: 'urne',
  interactionType: 'interact',
  mapCategory: 'chest',
  effects: [
    { handler: 'playSound', params: { id: 'urn' } },
    {
      handler: 'spawnItemsFromPool',
      params: { pool: 'map-loot', maxCount: 2, radius: 1 },
    },
    { handler: 'markPoiUsed' },
    { handler: 'triggerPoiBreakAnimation' },
  ],
};

export const LOCATION_RETURN_SANCTUARY: LocationDefinition = {
  id: 'return-sanctuary',
  width: 2,
  height: 2,
  placement: 'floor',
  weight: 0,
  unique: true,
  sprite: 'build2',
  interactionType: 'interact',
  mapCategory: 'altar',
  effects: [
    { handler: 'returnToHub', params: {} },
    { handler: 'markPoiUsed', params: {} },
  ],
};

// ---------------------------------------------------------------------------
// Standard floor locations
// ---------------------------------------------------------------------------

export const LOCATION_REST_SPRING: LocationDefinition = {
  id: 'rest-spring',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 6,
  unique: true,
  oncePerPlayer: true,
  sprite: 'source_onirique',
  interactionType: 'interact',
  mapCategory: 'spring',
  effects: [
    { handler: 'healShield', params: { amount: 20 } },
    { handler: 'healHPPercent', params: { percent: 0.25 } },
    { handler: 'markPoiUsed' },
  ],
};

export const LOCATION_BOOST_ALTAR: LocationDefinition = {
  id: 'boost-altar',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 8,
  unique: true,
  oncePerPlayer: true,
  sprite: 'build1',
  interactionType: 'interact',
  mapCategory: 'altar',
  effects: [
    { handler: 'spawnBoost', params: { source: 'altar' } },
    { handler: 'markPoiUsed' },
  ],
};

export const LOCATION_GRIMOIRE_SHRINE: LocationDefinition = {
  id: 'grimoire-shrine',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 5,
  unique: true,
  oncePerPlayer: true,
  sprite: 'shrine',
  interactionType: 'interact',
  mapCategory: 'altar',
  effects: [{ handler: 'spawnRandomGrimoire' }, { handler: 'markPoiUsed' }],
};

export const LOCATION_MERCHANT: LocationDefinition = {
  id: 'merchant',
  width: 2,
  height: 1,
  placement: 'floor',
  weight: 5,
  unique: true,
  artHeight: 1.5,
  minMonsterLevel: 14,
  sprite: 'wandering_merchant',
  interactionType: 'interact',
  mapCategory: 'shop',
  effects: [{ handler: 'openInterface', params: { id: 'wandering-merchant' } }],
};

// ---------------------------------------------------------------------------
// Traps
// ---------------------------------------------------------------------------

export const LOCATION_SPIKE_TRAP: LocationDefinition = {
  id: 'spike-trap',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 25,
  maxCount: 3,
  isTrap: true,
  sprite: 'spike-trap',
  interactionType: 'walk',
  mapCategory: 'trap',
  effects: [
    { handler: 'playSound', params: { id: 'trap-spike' } },
    { handler: 'dealSelfDamage', params: { amount: 10, type: 'physical' } },
    { handler: 'markPoiUsed' },
    { handler: 'triggerPoiBreakAnimation' },
  ],
};

export const LOCATION_SLOW_TRAP: LocationDefinition = {
  id: 'slow-trap',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 15,
  maxCount: 2,
  isTrap: true,
  sprite: 'slow-trap',
  interactionType: 'walk',
  mapCategory: 'trap',
  effects: [
    { handler: 'applyBuff', params: { buffId: 'trap-slow' } },
    { handler: 'markPoiUsed' },
    { handler: 'triggerPoiBreakAnimation' },
  ],
};

export const LOCATION_ALARM_TRAP: LocationDefinition = {
  id: 'alarm-trap',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 12,
  maxCount: 2,
  isTrap: true,
  sprite: 'alarm-trap',
  interactionType: 'walk',
  mapCategory: 'trap',
  effects: [
    { handler: 'playSound', params: { id: 'trap-alarm' } },
    { handler: 'spawnBiomeMonsters', params: { count: 2 } },
    { handler: 'markPoiUsed' },
    { handler: 'triggerPoiBreakAnimation' },
  ],
};

export const LOCATION_WARP_TRAP: LocationDefinition = {
  id: 'warp-trap',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 10,
  maxCount: 2,
  isTrap: true,
  sprite: 'warp-trap',
  interactionType: 'walk',
  mapCategory: 'trap',
  effects: [
    { handler: 'playSound', params: { id: 'trap-warp' } },
    { handler: 'teleportRandom', params: {} },
    { handler: 'markPoiUsed' },
    { handler: 'triggerPoiBreakAnimation' },
  ],
};

// ---------------------------------------------------------------------------
// Risk-pact
// ---------------------------------------------------------------------------

export const LOCATION_RISK_PACT: LocationDefinition = {
  id: 'risk-pact',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 5,
  unique: true,
  oncePerPlayer: true,
  sprite: 'circle',
  interactionType: 'interact',
  mapCategory: 'altar',
  effects: [
    { handler: 'applyBuff', params: { buffId: 'pact-malus' } },
    { handler: 'applyBuff', params: { buffId: 'pact-bonus' } },
    { handler: 'markPoiUsed' },
  ],
};

// ---------------------------------------------------------------------------
// Template-only locations (vault, challenge, lairs)
// ---------------------------------------------------------------------------

export const LOCATION_VAULT: LocationDefinition = {
  id: 'vault',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 0,
  templateOnly: true,
  sprite: 'coffre',
  interactionType: 'interact',
  mapCategory: 'chest',
  requiresKey: true,
  effects: [
    {
      handler: 'spawnWeightedPool',
      params: { pool: 'treasure', radius: 1 },
    },
    { handler: 'markPoiUsed' },
    { handler: 'triggerPoiBreakAnimation' },
  ],
};

export const LOCATION_CHALLENGE_TRIGGER: LocationDefinition = {
  id: 'challenge-trigger',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 0,
  templateOnly: true,
  sprite: 'mind-trap',
  interactionType: 'interact',
  effects: [
    { handler: 'spawnChallengeWave', params: { count: 4 } },
    { handler: 'markPoiUsed' },
  ],
};

export const LOCATION_CHALLENGE_CHEST: LocationDefinition = {
  id: 'challenge-chest',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 0,
  templateOnly: true,
  sprite: 'coffre',
  interactionType: 'interact',
  mapCategory: 'chest',
  effects: [
    { handler: 'playSound', params: { id: 'chest' } },
    {
      handler: 'spawnWeightedPool',
      params: { pool: 'challenge-treasure', radius: 1 },
    },
    { handler: 'spawnBoost', params: { source: 'challenge' } },
    { handler: 'markPoiUsed' },
    { handler: 'triggerPoiBreakAnimation' },
  ],
};

export const LOCATION_LAIR_KITSUNE: LocationDefinition = {
  id: 'lair-kitsune',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 0,
  templateOnly: true,
  oncePerRun: true,
  floorRange: [3, 6],
  sprite: 'mind-trap',
  interactionType: 'walk',
  miniBossId: 'kitsune',
  effects: [{ handler: 'markPoiUsed' }],
};

export const LOCATION_LAIR_JINN: LocationDefinition = {
  id: 'lair-jinn',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 0,
  templateOnly: true,
  oncePerRun: true,
  floorRange: [3, 8],
  sprite: 'mind-trap',
  interactionType: 'walk',
  miniBossId: 'jinn',
  effects: [{ handler: 'markPoiUsed' }],
};

export const LOCATION_LAIR_PARASITE: LocationDefinition = {
  id: 'lair-parasite',
  width: 1,
  height: 1,
  placement: 'floor',
  weight: 0,
  templateOnly: true,
  oncePerRun: true,
  floorRange: [4, 10],
  sprite: 'mind-trap',
  interactionType: 'walk',
  miniBossId: 'parasite',
  effects: [{ handler: 'markPoiUsed' }],
};

// ---------------------------------------------------------------------------
// Aggregated lists
// ---------------------------------------------------------------------------

/**
 * Locations placed naturally by the generator in procedural rooms.
 * `templateOnly` locations are excluded.
 */
/**
 * Reusable common location pool, composed into biomes' `possibleLocations`
 * (see `data/biomes.config.ts`). Biomes that want a different mix can omit it
 * or extend it.
 */
export const DEFAULT_LOCATIONS: LocationDefinition[] = [
  LOCATION_STAIRS_DOWN,
  LOCATION_SPAWN_POINT,
  LOCATION_CHEST,
  LOCATION_URN,
  LOCATION_RETURN_SANCTUARY,
  LOCATION_REST_SPRING,
  LOCATION_BOOST_ALTAR,
  LOCATION_GRIMOIRE_SHRINE,
  LOCATION_MERCHANT,
  LOCATION_SPIKE_TRAP,
  LOCATION_SLOW_TRAP,
  LOCATION_ALARM_TRAP,
  LOCATION_WARP_TRAP,
  LOCATION_RISK_PACT,
];

export const BOSS_FLOOR_LOCATIONS: LocationDefinition[] = [
  LOCATION_SPAWN_POINT,
];

const TEMPLATE_ONLY_LOCATIONS: LocationDefinition[] = [
  LOCATION_VAULT,
  LOCATION_CHALLENGE_TRIGGER,
  LOCATION_CHALLENGE_CHEST,
  LOCATION_LAIR_KITSUNE,
  LOCATION_LAIR_JINN,
  LOCATION_LAIR_PARASITE,
];

/**
 * Complete registry of known Locations (including `templateOnly` ones),
 * used to resolve IDs referenced in RoomTemplates or to retrieve the effects
 * of a triggered location.
 */
export const LOCATION_REGISTRY: Record<string, LocationDefinition> =
  Object.fromEntries(
    [
      ...DEFAULT_LOCATIONS,
      ...BOSS_FLOOR_LOCATIONS,
      ...TEMPLATE_ONLY_LOCATIONS,
    ].map((l) => [l.id, l]),
  );
